import React, { KeyboardEvent } from 'react';
import { Popup } from '@progress/kendo-react-popup';
import { MenuItem } from '@progress/kendo-react-layout';
import { Component } from '../../../dispatcher/modules';
import { Position } from '../../../dispatcher/Types';
import Container from '../../layout/container/Container';
import PuzzleButton from '../../UI/button/PuzzleButton';
import PuzzleMenu from '../../layout/menu/PuzzleMenu';
import PuzzleFakeCheckbox from '../../UI/checkbox/PuzzleFakeCheckbox';
import { PUZZLE_BUTTON_PROPS } from '../../UI/button/Types';
import { 
  COLUMN_ITEM_PROPS,
  COLUMNS_BUTTON_PROPS,
  COLUMNS_BTN_STATE 
} from './Types';
import { PUZZLE_COMPONENT_TOOLTIP_PROPS } from '../../UI/fields/Types';
import { Keys } from '@progress/kendo-react-common';
import { popupMenuWrapperHandleTabKeyDown } from '../../../utility';

/**
 * @class ColumnsButton
 * A button with a list of all grid columns
 * Changing columns visibility in the button popup will reconfigure grid columns
 */
class ColumnsButton extends Component<COLUMNS_BUTTON_PROPS, COLUMNS_BTN_STATE> {
  state:COLUMNS_BTN_STATE = {
    dropDownVisibility: false
  };

  static defaultProps = {
    withText: true
  }

  /**
   * Component button, container and div refs
   */
  private buttonRef: React.RefObject<PuzzleButton | null> = React.createRef();
  private anchorRef: React.RefObject<HTMLDivElement> | null = React.createRef();
  private contentRef: React.RefObject<HTMLDivElement> | null = React.createRef();

  /**
   * On Escape keyboard button close popup
   * And handle tab and shift + tab keyboard navigation through popup
   * @param {React.KeyboardEvent} event
   */
  private onKeyDown = (event: React.KeyboardEvent) => {
    switch (event.keyCode) {
      case Keys.esc:
        this.toggleDropDownVisibility();
        break;
      case Keys.tab:
        popupMenuWrapperHandleTabKeyDown(event, this.contentRef);
        break;
      default:
        break;
    }
  };

  /**
   * Button menu item renderer
   * All items will be rendered as checkboxes and set in state
   * @param {COLUMN_ITEM_PROPS} props Drop down item props
   */
  private itemRenderer = (props: any) => {
    const { data } = props.item;

    return (
      <PuzzleFakeCheckbox
        checked={!data.hidden}
        label={this.translate(data.header)}
      />
    );
  };

  /**
   * Component drop-down blur event
   * If mouse click target is not contained within component el then drop-down el will be hidden
   * @param {Event} e Blur event object
   */
  private onBlur = ({ currentTarget, relatedTarget }: React.FocusEvent<HTMLDivElement>) => {
    if (!currentTarget.contains(relatedTarget)) {
      this.setState({ dropDownVisibility: false });
    }
  };

  /**
   * Handles drop down item click
   * Item click will set/unset column hidden prop and it will dispatch action to reconfigure grid columns
   */
  private onSelect = ({ item }) => {
    const columns = this.props.columns.slice() as COLUMN_ITEM_PROPS[];
    const index = columns.findIndex(column => column.dataIndex == item.data.dataIndex);
    let clickedColumn = columns[index];

    if (clickedColumn) {
      clickedColumn = { ...clickedColumn };
      clickedColumn.hidden = !clickedColumn.hidden;
      columns[index] = clickedColumn;

      this.dispatch('ReconfigureColumns', Position.end, {
        columns,
        gridModuleId: this.props.gridModuleId
      });
    }
  };

  /**
   * Toggles dropdown visibility state.
   */
  private toggleDropDownVisibility = (preventFocus = false) => {
    this.setState(({ dropDownVisibility }: any) => ({
      dropDownVisibility: !dropDownVisibility
    }), () => {
      if (preventFocus === false) {
        this.buttonRef.current.buttonEl.focus();
      }
    });
  };

  private renderMenuItem = (column: any, index: number, columns) => {
    const groupName = column.groupName;
    const meniItem =
      (
        <MenuItem
          cssClass={column.hidden ? '' : 'k-selected'}
          data={column}
          key={`grid-config-item-${column.dataIndex}`}
          render={this.itemRenderer}
        />);

    const renderGroupName = column.groupName && (groupName != columns[index - 1].groupName);

    if (!renderGroupName) {
      return meniItem;
    }
    else {
      const meniItemWithGroupName =
        [<MenuItem
          text={column.groupName}
          disabled={true}
        />];
      meniItemWithGroupName.push(meniItem);
      return meniItemWithGroupName;
    }
  };

  /**
   * Open Popup
   */
  private openPopup = () => {
    if (!this.state.dropDownVisibility) {
      this.setState({
        dropDownVisibility: true
      });
    }
  }

  /**
   * Open popup on press key Enter or press key space
   * @param {KeyboardEvent} e
   */
  private openPopupWithKeyboard = (e: KeyboardEvent) => {
    const { keyCode } = e;
    if (Keys.enter === keyCode || Keys.space === keyCode) {
      this.openPopup();
    }
  }

  /**
   * @override
   */
  renderBody() {
    const { withText = true, buttonText } = this.props;
    let { columns } = this.props;
    columns = columns.filter(column => !column.disabled);
    const tooltipProps: PUZZLE_COMPONENT_TOOLTIP_PROPS = !withText ? {
      withTooltip: true,
      titleText: buttonText || this.translate('buttonText'),
      tooltipPosition: 'left'
    } : {};

    const buttonProps:PUZZLE_BUTTON_PROPS = {
      ['aria-label']: this.translate('ariaLabel'),
      className: 'tp-color-accent',
      ['data-cy']: 'PuzzleButtonColumns',
      fillMode: 'outline',
      iconClass: 'tp-icon tp-icon-sm tp-columns tp-color-accent',
      onKeyDown: this.openPopupWithKeyboard,
      onMouseDown: this.openPopup,
      ref: this.buttonRef,
      themeColor: 'primary',
      ...tooltipProps
    };
    return (
      <Container
        displayInlineFlex
        ref={this.anchorRef}
      >
        {withText ? <PuzzleButton 
          {...buttonProps}
        >{buttonText || this.translate('buttonText')}</PuzzleButton> : <PuzzleButton
          {...buttonProps}
        />}
        <Popup
          anchor={this.anchorRef.current}
          anchorAlign={{ horizontal: 'right', vertical: 'bottom' }}
          popupAlign={{ horizontal: 'right', vertical: 'top' }}
          popupClass={'tp-menu-with-overflow'}
          show={this.state.dropDownVisibility}
        >
          <div
            className="tp-outline-none"
            data-cy={'popup-div'}
            onBlur={this.onBlur}
            onKeyDown={this.onKeyDown}
            tabIndex={0}
            ref={this.contentRef}
          >
            <PuzzleMenu
              onSelect={this.onSelect}
              vertical
            >
              {columns.map(this.renderMenuItem)}
            </PuzzleMenu>
          </div>
        </Popup>
      </Container>
    );
  }
}

export default ColumnsButton;
